import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { cn } from '../../lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded font-semibold ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-60 duration-0 cursor-pointer',
  {
    variants: {
      variant: {
        // TODO: Review flat text color — fill is surface-bg-inverse (dark)
        // but no text-* class is set, so labels inherit dark text and disappear in
        // Storybook/AllVariants. Call sites today are icon-only (sales-sheet upload);
        // decide whether flat should set text-header-inverse (like core) or stay
        // icon-only with callers owning label color.
        flat: 'rounded-md border border-border-inverse bg-surface-bg-inverse shadow-[var(--contrast-screen-shadow)]',
        core: 'bg-dark-bg text-header-inverse hover:bg-background-inverse border border-border-inverse/50',
        screen: 'bg-sub-screen-bg text-header border border-border hover:bg-[rgb(var(--sub-screen-bg)/8%)]',
        primary: 'bg-primary-accent text-header hover:bg-primary-accent/80',
        secondary: 'bg-background hover:bg-sub-surface-bg text-header border border-border',
        contrast:
          'border border-border-inverse bg-sub-screen-bg text-primary-inverse shadow-[var(--contrast-screen-shadow)] hover:bg-[rgb(var(--sub-screen-bg)/8%)]',
        error: 'bg-error text-header-inverse hover:bg-error',
        tertiary: 'bg-primary text-header-inverse hover:bg-primary/80',
        text: 'text-secondary',
        outline: 'border border-border bg-background shadow-sm hover:bg-sub-surface-bg hover:text-header',
        ghost: 'hover:bg-sub-surface-bg hover:text-header',
        // TODO: Review bordered `w-full` — this variant is used as a
        // full-width field trigger (date-picker, date-picker-with-range, change-
        // advertiser modal). Best practice: keep visual treatment on the
        // variant and put layout (`w-full`) on the call site (or a layout prop),
        // so Storybook/other uses don't stretch by default. Confirm with design
        // before moving `w-full` off the variant.
        bordered:
          'border-solid border-border border h-14 w-full hover:border-border/80 justify-start text-left font-number rounded text-sm text-header',
      },
      size: {
        large: 'h-16 py-4 px-10 text-lg',
        default: 'h-14 py-4 px-8 text-lg',
        small: 'h-[46px] py-4 px-6 text-base',
        tiny: 'h-[33px] py-0 px-4 text-xs [&>svg]:size-3.5',
        // Content-sized text actions, such as links in table cells.
        inline: 'h-auto p-0',
      },
      contrast: {
        true: '',
        false: '',
      },
      borderless: {
        true: '',
        false: '',
      },
      iconOnly: {
        true: 'aspect-square shrink-0 px-0',
        false: '',
      },
    },
    compoundVariants: [
      {
        variant: 'screen',
        contrast: true,
        class:
          'bg-sub-screen-bg-inverse text-header-inverse border-border-inverse hover:bg-[rgb(var(--sub-screen-bg-inverse)/8%)] hover:border-border/80',
      },
      {
        variant: ['screen', 'secondary'],
        borderless: true,
        class: 'border-transparent',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
      contrast: false,
      borderless: false,
      iconOnly: false,
    },
  },
)

export type ButtonSize = VariantProps<typeof buttonVariants>['size']
export type ButtonVariant = VariantProps<typeof buttonVariants>['variant']

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, contrast, borderless, iconOnly, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            contrast,
            borderless,
            iconOnly,
            className,
          }),
        )}
        ref={ref}
        {...props}
      >
        {children}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
